//@flow
import React, {useState} from "react";
import {useHistory} from "react-router-dom";
import {useObserver} from "mobx-react-lite";
import {useFormik} from "formik";
import {Col, Form, Input, Row, Button} from "antd";
import {useTranslation} from "react-i18next";
import {useStore} from "../../hooks/useStore";
import {loginFormValidations} from "./loginFormValidations";
import {ForgotPasswordModal} from "./ForgotPasswordModal/ForgotPasswordModal";
import {CardContainer, LoginWrapper} from "./loginStyles";
import {loginFormType} from "./loginFormPropTypes";
import {CustomButton} from "../_defaultComponents/CustomButton/CustomButton";
import {getStartRoute} from "../../utils/getStartRoute";
import {LoginLayout} from "../layouts/loginLayout/LoginLayout";
import {LoginBackground} from "./loginBackground/LoginBackground";

export const LoginFormRedesign = () => {
    const [forgotPasswordVisible, setForgotPasswordVisible] = useState(false);
    const {t, i18n} = useTranslation(["loginForm"]);
    const history = useHistory();
    const stores = useStore();
    const loginHandle = async (values: loginFormType) => {
        const result = await stores.meStore.login(values);

        if (result) {
            if (stores.appStore.beforeLogin) {
                history.push(stores.appStore.beforeLogin);
                stores.appStore.setBeforeLogin("");
            } else {
                history.push(
                    getStartRoute({
                        role: stores?.meStore?.role,
                        isPoolCustomer: stores?.meStore?.isPoolCustomer,
                        isUatCustomer: stores?.meStore?.isUatCustomer
                    })
                );
            }
        }
    };

    const formik = useFormik({
        initialValues: {
            username: "",
            password: ""
        },
        validationSchema: loginFormValidations,
        onSubmit: loginHandle
    });
    const {values, errors, touched, handleChange, handleSubmit, isSubmitting} = formik;

    return useObserver(() => (
        <LoginLayout>
            <LoginWrapper>
                <ForgotPasswordModal
                    isVisible={forgotPasswordVisible}
                    setVisible={() => setForgotPasswordVisible(!forgotPasswordVisible)}
                />
                <LoginBackground />
                <CardContainer
                    type="inner"
                    title={
                        <Row justify="space-between">
                            {t("formTitle", {ns: "loginForm"})}
                            <Button
                                size="small"
                                onClick={() =>
                                    i18n.changeLanguage(
                                        i18n.resolvedLanguage === "en" ? "tr" : "en"
                                    )
                                }
                            >
                                {i18n.resolvedLanguage === "en" ? "EN" : "TR"}
                            </Button>
                        </Row>
                    }
                    data-testid="loginForm"
                >
                    <Form
                        autoComplete="off"
                        onFinish={handleSubmit}
                        labelCol={{span: 24}}
                        wrapperCol={{span: 24}}
                        style={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 40px"
                        }}
                    >
                        <div
                            style={{
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center"
                            }}
                        >
                            <div
                                style={{
                                    width: "100%",
                                    textAlign: "center",
                                    marginBottom: "30px"
                                }}
                            >
                                <h2
                                    style={{
                                        fontWeight: "bold",
                                        fontSize: "36px",
                                        paddingTop: "50px"
                                    }}
                                >
                                    Kullanıcı Girişi
                                </h2>
                                <p
                                    style={{
                                        fontSize: "18px",
                                        marginTop: "15px"
                                    }}
                                >
                                    Vigo Uygulaması'nda kayıtlı olan e-posta adresinizi ve şifrenizi
                                    yazın.
                                </p>
                            </div>

                            <div
                                style={{
                                    width: "100%",
                                    maxWidth: "450px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "20px"
                                }}
                            >
                                <Form.Item
                                    label={t("usernameLabel", {ns: "loginForm"})}
                                    validateStatus={
                                        errors.username && touched.username ? "error" : "success"
                                    }
                                    help={
                                        errors.username && touched.username ? errors.username : null
                                    }
                                >
                                    <Input
                                        name="username"
                                        value={values.username}
                                        onChange={handleChange}
                                        data-testid="usernameInput"
                                        size="large"
                                        style={{
                                            borderRadius: "10px",
                                            height: "50px",
                                            fontSize: "16px"
                                        }}
                                    />
                                </Form.Item>

                                <Form.Item
                                    label={t("passwordLabel", {ns: "loginForm"})}
                                    validateStatus={
                                        errors.password && touched.password ? "error" : "success"
                                    }
                                    help={
                                        errors.password && touched.password ? errors.password : null
                                    }
                                >
                                    <Input.Password
                                        name="password"
                                        value={values.password}
                                        onChange={handleChange}
                                        data-testid="passwordInput"
                                        size="large"
                                        style={{
                                            borderRadius: "10px",
                                            height: "50px",
                                            fontSize: "16px"
                                        }}
                                    />
                                </Form.Item>

                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "20px",
                                        marginTop: "20px"
                                    }}
                                >
                                    <CustomButton
                                        block
                                        disabled={isSubmitting}
                                        style={{
                                            backgroundColor: "#aeff01",
                                            borderRadius: "100px",
                                            padding: "25px",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            fontWeight: "bold",
                                            fontSize: "16px"
                                        }}
                                        htmlType="submit"
                                        data-testid="loginButton"
                                    >
                                        {t("loginButton", {ns: "loginForm"})}
                                    </CustomButton>

                                    <CustomButton
                                        block
                                        data-testid="forgotPasswordButton"
                                        onClick={() => {
                                            setForgotPasswordVisible(true);
                                        }}
                                        style={{
                                            borderRadius: "100px",
                                            padding: "25px",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            border: "1px solid #DDDDDD",
                                            fontWeight: "bold",
                                            fontSize: "16px"
                                        }}
                                    >
                                        {t("forgotPasswordButton", {ns: "loginForm"})}
                                    </CustomButton>
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        marginTop: "30px",
                                        fontSize: "16px",
                                        color: "#A3A3A3"
                                    }}
                                >
                                    Eğer Vigo ailesine katılmak istiyorsanız
                                    <a
                                        style={{
                                            color: "#1A1A1A",
                                            borderBottom: "1px solid #1A1A1A",
                                            marginLeft: "5px",
                                            fontWeight: "500"
                                        }}
                                        href="https://vigovigo.com/kuryeye-ihtiyacim-var/"
                                    >
                                        Üye olun.
                                    </a>
                                </div>
                            </div>
                        </div>
                    </Form>
                </CardContainer>
            </LoginWrapper>
        </LoginLayout>
    ));
};
